import { OwnerDataCups } from "../../../../core/models/ownerData/ownerData.model";
import FilterCups from "../../../FilterCups/FilterCups";
import {
  HistoricFilters,
  ManagementCodeText,
  MasterFiltersManagementCups,
} from "../../../../core/models/managements/managements.model";
import { useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const LIMIT_TAGS = 1;

interface ManagementHistoricFilterTypeProps {
  subtypes: ManagementCodeText[];
  setFilters: React.Dispatch<React.SetStateAction<HistoricFilters>>;
  filters: HistoricFilters;
}

const ManagementHistoricFilterType = ({
  subtypes,
  setFilters,
  filters,
}: ManagementHistoricFilterTypeProps) => {
  const { t } = useTranslation();

  return (
    <FormControl>
      <FormLabel
        sx={{
          fontWeight: "600",
          paddingBottom: ".8rem",
        }}
      >
        {t("Tipo de gestión")}
      </FormLabel>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={subtypes}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) =>
          JSON.stringify(option.id.sort()) === JSON.stringify(value.id.sort())
        }
        value={subtypes.filter((option) =>
          filters.type.some((item) => option.id.includes(item))
        )}
        noOptionsText={t("Tablas.No hay coincidencias")}
        onChange={(event, value) => {
          setFilters((prevFilters: HistoricFilters) => {
            return {
              ...prevFilters,
              type: value.flatMap((option) => option.id),
            };
          });
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...otherProps } = props;
          return (
            <ListItem key={key} {...otherProps}>
              <Checkbox
                icon={<span className="t-icon-checkbox-default" />}
                checkedIcon={
                  <span className="t-icon t-icon-checkbox-checked" />
                }
                sx={{ fontSize: "24px" }}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {t(`Gestiones.${option.label}`)}
            </ListItem>
          );
        }}
        renderTags={(value, getTagProps) => (
          <>
            {value.slice(0, LIMIT_TAGS).map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={index}
                label={
                  <div
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 200,
                    }}
                  >
                    {t(`Gestiones.${option.label}`)}
                  </div>
                }
              />
            ))}
            {value.length > LIMIT_TAGS && ` +${value.length - LIMIT_TAGS}`}
          </>
        )}
        style={{
          width: "100%",
        }}
        renderInput={(params) => (
          <TextField
            sx={{
              width: "100%",
            }}
            inputProps={{
              ...params.inputProps,
              style: {
                boxShadow: "none",
              },
            }}
            InputProps={{
              ...params.InputProps,
              fullWidth: true,
              endAdornment: (
                <Typography
                  component="span"
                  className="t-custom-dropdown t-icon-arrow-down"
                  data-testid="dropdown-arrow"
                  sx={{
                    color: "primary.main",
                    position: "absolute",
                    right: "12px",
                    pointerEvents: "none",
                  }}
                />
              ),
            }}
            placeholder={t(
              `${
                filters.type.length
                  ? "Tipo de gestión"
                  : "Gestiones.Todas las gestiones"
              }`
            )}
          />
        )}
      />
    </FormControl>
  );
};

interface ManagementHistoricFiltersProps {
  ownerDataCups: OwnerDataCups[];
  searchParamsSearchBar: HistoricFilters;
  setSearchParamsSearchBar: React.Dispatch<React.SetStateAction<any>>;
  masterFiltersSubtypes: number[];
  masterFiltersCups: MasterFiltersManagementCups[];
  allSubTypes: ManagementCodeText[];
}

const ManagementHistoricFilters = ({
  ownerDataCups,
  searchParamsSearchBar,
  setSearchParamsSearchBar,
  masterFiltersSubtypes,
  masterFiltersCups,
  allSubTypes,
}: ManagementHistoricFiltersProps) => {
  const [firstInputChanged, setFirstInputChanged] = useState<string>("");
  const cupsFilters = {
    cups: searchParamsSearchBar.cups,
    socialReason: searchParamsSearchBar.socialReason,
  };

  const currentSubTypes: ManagementCodeText[] = allSubTypes.filter((item) =>
    masterFiltersSubtypes.some((subtype) =>
      item.id.some((id) => id === subtype)
    )
  );

  return (
    <Box
      component="div"
      display="flex"
      flexDirection="column"
      gap="2rem"
      width="100%"
      justifyContent={"space-between"}
    >
      <FilterCups
        masterFiltersCups={masterFiltersCups}
        filters={cupsFilters}
        setFilters={setSearchParamsSearchBar}
        firstInputChanged={firstInputChanged}
        setFirstInputChanged={setFirstInputChanged}
      />
      <ManagementHistoricFilterType
        subtypes={currentSubTypes}
        filters={searchParamsSearchBar}
        setFilters={setSearchParamsSearchBar}
      />
    </Box>
  );
};

export default ManagementHistoricFilters;
